import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import { foldIncludes } from "../lib/trSearch";

const GAP = 4;
const MAX_H = 480;
const MIN_H = 220;   // altta bu kadar yer yoksa menü yukarı açılır

// Menü body'ye portal ile, sabit konumda açılır: kart/tablo taşmalarıyla kırpılmaz, ekranda kalan alana sığar (gerekirse yukarı açılır), liste kendi içinde kayar
function useDropdownPos(open, anchorRef, deps) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const calc = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth, vh = window.innerHeight;
      const below = vh - r.bottom - GAP - 8, above = r.top - GAP - 8;
      const up = below < MIN_H && above > below;
      const maxHeight = Math.min(MAX_H, Math.max(120, up ? above : below));
      const minWidth = Math.min(Math.max(r.width, 320), vw - 16);
      const left = Math.max(8, Math.min(r.left, vw - minWidth - 8));
      const maxWidth = Math.min(640, vw - left - 8);   // sağdan taşmaz
      setPos(up ? { left, bottom: vh - r.top + GAP, maxHeight, minWidth, maxWidth } : { left, top: r.bottom + GAP, maxHeight, minWidth, maxWidth });
    };
    calc();
    window.addEventListener("scroll", calc, true);
    window.addEventListener("resize", calc);
    return () => { window.removeEventListener("scroll", calc, true); window.removeEventListener("resize", calc); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ...deps]);
  return pos;
}

export default function SearchableSelect({ options, value, onChange, placeholder = "Seçiniz…", disabled = false, testid, footer, onOpen }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef(null);
  const dropRef = useRef(null);
  const listRef = useRef(null);
  const selected = options.find((o) => o.value === value);
  const filtered = options.filter((o) => foldIncludes(o.label, q));
  const pos = useDropdownPos(open, ref, [filtered.length]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target) && !dropRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => { setActiveIdx(-1); }, [q, open]);
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) listRef.current.children[activeIdx]?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const focusNext = () => {
    const input = ref.current?.querySelector("input");
    if (!input) return;
    const els = [...document.querySelectorAll("input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), button:not([disabled])")]
      .filter((el) => el.tabIndex >= 0 && el.offsetParent !== null);
    const i = els.indexOf(input);
    if (i >= 0 && els[i + 1]) els[i + 1].focus();
  };

  const pickAt = (idx, advance = false) => {
    const o = filtered[idx];
    if (!o) return;
    onChange(o.value); setOpen(false); setQ("");
    if (advance) setTimeout(focusNext, 0);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      if (activeIdx < 0 && !q) { setOpen(false); setTimeout(focusNext, 0); return; }   // seçim yapılmadan Enter: değeri koru, yan kutuya geç
      pickAt(activeIdx >= 0 ? activeIdx : (filtered.length ? 0 : -1), true);
    }
    else if (e.key === " " && activeIdx >= 0) { e.preventDefault(); pickAt(activeIdx, true); }
    else if (e.key === "Tab") {
      const idx = activeIdx >= 0 ? activeIdx : (q && filtered.length ? 0 : -1);
      if (idx >= 0) pickAt(idx); else { setOpen(false); setQ(""); }
    }
    else if (e.key === "Escape") { setOpen(false); setQ(""); e.target.blur(); }
  };

  const dropdown = open && !disabled && pos && createPortal(
    <div ref={dropRef} data-searchable="1" data-testid={`${testid}-dropdown`}
      className="fixed z-[70] flex flex-col w-max bg-white border border-slate-200 rounded-md shadow-xl"
      style={{ left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, minWidth: pos.minWidth, maxWidth: pos.maxWidth }}>
      <div className="shrink-0 px-3 py-1.5 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
        {q ? `${filtered.length} sonuç bulundu` : `Tüm listeden seç · ${filtered.length} kayıt`}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" ref={listRef} data-testid={`${testid}-list`}>
        {filtered.map((o, idx) => (
          <button
            key={o.value}
            type="button"
            data-testid={`${testid}-option-${o.value}`}
            onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
            className={`w-full text-left px-3.5 py-2.5 text-sm leading-snug whitespace-normal break-words hover:bg-amber-50 transition-colors ${o.value === value ? "bg-amber-50 font-semibold" : ""} ${idx === activeIdx ? "bg-amber-100 font-semibold" : ""}`}
          >
            {o.label}
          </button>
        ))}
        {filtered.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">Sonuç bulunamadı</div>}
      </div>
      {footer && (
        <button
          type="button"
          data-testid={footer.testid || `${testid}-footer`}
          onClick={() => { setOpen(false); setQ(""); footer.onClick(); }}
          className="shrink-0 w-full text-left px-3 py-2 text-xs font-semibold text-amber-700 border-t border-slate-100 hover:bg-amber-50 flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> {footer.label}
        </button>
      )}
    </div>,
    document.body,
  );

  return (
    <div ref={ref} className="relative" data-searchable="1">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          disabled={disabled}
          data-testid={testid}
          value={open ? q : selected ? selected.label : ""}
          placeholder={placeholder}
          onFocus={() => { if (!disabled) { setOpen(true); setQ(""); onOpen?.(); } }}
          onClick={() => { if (!disabled) { setOpen(true); onOpen?.(); } }}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            if (e.target.value === "") onChange("");
          }}
          onKeyDown={onKeyDown}
          data-testid-input={`${testid}-kbd`}
          title="↑↓ gez · Boşluk/Enter seç ve sonraki kutuya geç · Tab seç ve geç"
          className="w-full pl-8 pr-8 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none disabled:bg-slate-50 disabled:text-slate-400 transition-colors"
        />
        {selected && !disabled && (
          <button type="button" tabIndex={-1} data-testid={`${testid}-clear`} title="Temizle"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onChange(""); setQ(""); setOpen(false); }}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-600 transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
      </div>
      {dropdown}
    </div>
  );
}
